import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast, formatDate } from '@xdocs/i18n';
import type { ModerationStatus, ShareListRow, ShareVisibility } from '@xdocs/schemas';
import { Badge, Button, ConfirmDialog, DataTable, Tabs, cn, useI18n, useToast } from '@xdocs/ui';
import type { BadgeTone, ColumnDef } from '@xdocs/ui';
import { requestTyped } from '@xdocs/ws-protocol';
import { ShareDialog } from '../../features/sharing/index.js';
import { optionalProp } from '../../lib/optionalProp.js';
import { useWs } from '../../ws/WsProvider.js';
import { ReferralPanel } from './ReferralPanel.js';
import { useSharesList } from './useSharesList.js';
import { useSharesT } from './useSharesT.js';
import styles from './SharesPage.module.css';

const VISIBILITY_TONE: Record<ShareVisibility, BadgeTone> = {
  private: 'neutral',
  public: 'success',
  link: 'info',
  user: 'info',
};

const MODERATION_TONE: Record<ModerationStatus, BadgeTone> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
};

type SharesTab = 'shares' | 'referrals';

/**
 * `/shares` (F27/F62-64 management + F72) — "My shares" `<DataTable>` (own
 * visibility/moderation state, stats, revoke) plus a Referrals tab (F72).
 */
export function SharesPage(): ReactElement {
  const { locale } = useI18n();
  const { t } = useSharesT();
  const { wsClient } = useWs();
  const toast = useToast();
  const { rows, total, nextCursor, query, onQueryChange, onLoadMore, isLoading, isLoadingMore, isError, loadMoreError, onRetry, invalidate } = useSharesList();
  const [tab, setTab] = useState<SharesTab>('shares');
  const [manageRow, setManageRow] = useState<ShareListRow | null>(null);
  const [revokeRow, setRevokeRow] = useState<ShareListRow | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  async function handleRevoke(): Promise<void> {
    if (!revokeRow) return;
    setIsRevoking(true);
    setRevokeError(null);
    try {
      await requestTyped(wsClient, 'share.revoke', { shareId: revokeRow.id });
      toast.push(assetToast(locale, 'success', t('shares:toast.revoked.action'), revokeRow.documentTitle));
      invalidate();
      setRevokeRow(null);
    } catch (err) {
      const alert = err as { message?: string };
      setRevokeError(alert.message ?? t('shares:toast.revokeFailed'));
    } finally {
      setIsRevoking(false);
    }
  }

  const columns: ColumnDef<ShareListRow>[] = [
    {
      id: 'document',
      header: t('shares:list.column.document'),
      primary: true,
      cell: (row) => <span className={cn(styles['titleCell'])}>{row.documentTitle}</span>,
    },
    {
      id: 'visibility',
      header: t('shares:list.column.visibility'),
      cell: (row) => <Badge tone={VISIBILITY_TONE[row.visibility]}>{t(`shares:visibility.${row.visibility}`)}</Badge>,
    },
    {
      id: 'moderation',
      header: t('shares:list.column.moderation'),
      cell: (row) => (row.visibility === 'public' ? <Badge tone={MODERATION_TONE[row.moderationStatus]}>{t(`shares:moderation.${row.moderationStatus}`)}</Badge> : <span>—</span>),
    },
    {
      id: 'comments',
      header: t('shares:list.column.comments'),
      hideOnMobile: true,
      cell: (row) => <span>{row.commentCount}</span>,
    },
    {
      id: 'likes',
      header: t('shares:list.column.likes'),
      hideOnMobile: true,
      cell: (row) => <span>{row.likeCount}</span>,
    },
    {
      id: 'updatedAt',
      header: t('shares:list.column.updatedAt'),
      sortable: true,
      cell: (row) => <span className={cn(styles['metaCell'])}>{formatDate(locale, row.updatedAt, { dateStyle: 'medium' })}</span>,
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className={cn(styles['actionsCell'])}>
          <Button variant="ghost" size="sm" onClick={() => setManageRow(row)}>
            {t('shares:list.action.manage')}
          </Button>
          {row.visibility !== 'private' && (
            <Button variant="ghost" size="sm" onClick={() => setRevokeRow(row)}>
              {t('shares:list.action.revoke')}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <h1 className={cn(styles['title'])}>{t('shares:page.title')}</h1>
        <p className={cn(styles['description'])}>{t('shares:page.description')}</p>
      </div>

      <Tabs<SharesTab>
        label={t('shares:page.title')}
        value={tab}
        onChange={setTab}
        items={[
          { value: 'shares', label: t('shares:tabs.shares') },
          { value: 'referrals', label: t('shares:tabs.referrals') },
        ]}
      />

      {tab === 'shares' && (
        <DataTable
          variant="card"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          query={query}
          onQueryChange={onQueryChange}
          label={t('shares:page.title')}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
          total={total}
          nextCursor={nextCursor}
          onLoadMore={onLoadMore}
          isLoadingMore={isLoadingMore}
          {...optionalProp('loadMoreError', loadMoreError)}
          emptyTitle={t('shares:list.empty.title')}
          emptyDescription={t('shares:list.empty.description')}
          noResultsTitle={t('shares:list.noResults.title', { query: query.q ?? '' })}
          onClearFilters={() => {
            const { q: _q, ...rest } = query;
            onQueryChange({ ...rest, page: 1 });
          }}
          searchLabel={t('shares:list.searchLabel')}
          searchPlaceholder={t('shares:list.searchPlaceholder')}
        />
      )}

      {tab === 'referrals' && <ReferralPanel />}

      {manageRow && (
        <ShareDialog
          open
          onOpenChange={(open) => !open && setManageRow(null)}
          documentId={manageRow.documentId}
          documentTitle={manageRow.documentTitle}
          initialShare={manageRow}
        />
      )}

      <ConfirmDialog
        open={revokeRow !== null}
        onOpenChange={(open) => !open && setRevokeRow(null)}
        action={t('shares:list.action.revoke')}
        assetName={revokeRow?.documentTitle ?? ''}
        destructive
        loading={isRevoking}
        {...optionalProp('error', revokeError ?? undefined)}
        onConfirm={() => void handleRevoke()}
      />
    </div>
  );
}
